import { useCallback, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useQuery } from '@ts-core/lib/apollo';
import { useFilteredData } from '@ts-core/core/hooks/ui';

import type { Student } from 'modules/students/types';

import {
  getAssignmentResultsQuery,
  GetAssignmentResultsQueryResult,
  GetAssignmentResultsQueryVariables,
} from '../graphql/queries/getAssignmentResultsQuery';

import { formatValue, timeString } from '@ts-core/utils/dataTableUtils';

import { AssignmentResults } from '../types';

interface AssignmentResultsHookInput {
  courseId: string;
  assignmentId: string;
}

interface AssignmentResultsHookResult {
  results?: AssignmentResults;
  students?: Student[];
  loading?: boolean;
  onFilterData: (query: string) => void;

  onToggleShowOnlyStudentsWithoutAnswer: () => void;
  showOnlyStudentsWithoutAnswer: boolean;

  onGetXlsxData: (sortedData: { _original: any }[]) => void;
}

export default function useAssignmentResults(input: AssignmentResultsHookInput): AssignmentResultsHookResult {
  const { courseId, assignmentId } = input;
  const intl = useIntl();

  const [showOnlyStudentsWithoutAnswer, setShowOnlyStudentsWithoutAnswer] = useState(false);
  const onToggleShowOnlyStudentsWithoutAnswer = useCallback(() => {
    setShowOnlyStudentsWithoutAnswer((state) => !state);
  }, []);

  /**
   * Get results
   */
  const { data, loading } = useQuery<GetAssignmentResultsQueryResult, GetAssignmentResultsQueryVariables>(getAssignmentResultsQuery, {
    variables: { courseId, assignmentId },
    notifyError: true,
  });
  const results = data?.results;
  const exercises = results?.exercises;
  const allStudents = results?.students;
  const { filteredData, onFilterData } = useFilteredData({ items: allStudents, fields: ['name', 'email'] });
  const students = useMemo(() => {
    return filteredData?.filter((s) => !showOnlyStudentsWithoutAnswer || s.noAnswer);
  }, [filteredData, showOnlyStudentsWithoutAnswer]);

  /**
   * Get XLSX Data
   */
  const onGetXlsxData = useCallback(
    (sortedData: { _original: Student }[]) => {
      if (!exercises) return [];

      const headers = [
        intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        intl.formatMessage({ id: 'assignmentResultsTable.group' }),
        intl.formatMessage({ id: 'result' }),
        intl.formatMessage({ id: 'time' }),
      ];
      exercises?.forEach((e) => {
        e.questions?.forEach((q) => {
          headers.push(`${e.code} - ${q.name}`);
        });
      });

      const xlsData = [headers];

      sortedData.forEach((data) => {
        const student = data?._original as any;
        const answerData = [student.name, student.group || '-', formatValue(student.score, results.maxScore), timeString(student.time * 60)];
        exercises.forEach((e) => {
          e.questions?.forEach((q) => {
            const questionId = q._id as string;
            const qScore = student[questionId];
            answerData.push(qScore === undefined ? '-' : qScore);
          });
        });
        xlsData.push(answerData);
      });
      return xlsData;
    },
    [intl, exercises, results]
  );

  return {
    results,
    students,
    loading,
    onFilterData,

    onToggleShowOnlyStudentsWithoutAnswer,
    showOnlyStudentsWithoutAnswer,

    onGetXlsxData,
  };
}
